import {
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FC,
  type PropsWithChildren
} from 'react';
import { CSSTransition } from 'react-transition-group';
import { classes } from '@/next-utils/css-utils/scss-utils';
import { createPortal } from 'react-dom';
import { isNullish } from '@/utils/null-utils';
import { type IPopoverPosition, type IPopoverProps } from '../../IPopoverProps';
import { useCurtain } from '../../../Curtain/useCurtain';
import useConditionallyDisableScrolling from '../../../hooks/useConditionallyDisableScrolling';

import S from './styles.base.module.scss';

// If none is provided, the default anchor position is set to left and bottom.
const DEFAULT_POPOVER_POSITION: IPopoverPosition = { x: 'left', y: 'bottom' };

// If none is provided, the default grow direction is set to right and bottom.
const DEFAULT_POPOVER_DIRECTION: IPopoverPosition = { x: 'right', y: 'bottom' };

/**
 * A client-only implementation of the Popover component.
 * It can only be rendered on the client side because it relies on the `document` object.
 *
 * This component have some similarities with `<Modal />` component, but it behaves
 * differently in terms of positioning and animation, therefore it is implemented
 * as a separate component for better separation of concerns.
 *
 * If you need to display content in the screen blocking the screen behind it,
 * you can follow the below guidelines:
 *
 * - Choose `<Modal />` when:
 *   - You want to display a modal dialog appears at the center of the screen.
 *   - You want to display a modal dialog that covers the entire screen.
 *   - You want to display a panel that is fixed to the viewport left or right.
 * - Choose `<Popover />` when:
 *   - You want to display a popover that appears **anchored** to an element.
 *     - Relative can be left, right, top or bottom.
 */
export const ClientOnlyPopover: FC<PropsWithChildren<IPopoverProps>> = ({
  children,
  position = DEFAULT_POPOVER_POSITION,
  direction = DEFAULT_POPOVER_DIRECTION,
  isOpen,
  onClose,
  onCloseFinished,
  anchorRef,
  className,
  popoverClassName
}) => {
  const [computedStyle, setComputedStyle] = useState<CSSProperties>({});
  // The popoverRef is used to reference the popover element in the DOM.
  const popoverRef = useRef<HTMLDivElement | null>(null);
  // The contentRef used to perform measurements on the popover content.
  const contentRef = useRef<HTMLDivElement | null>(null);
  // The default transition duration for the modal.
  const popoverTransitionTimeout = 200;

  // Same as the modal, we need to conditionally disable scrolling when a popover is open.
  useConditionallyDisableScrolling(isOpen);

  // Use the curtain hook to render the curtain portal independently.
  //
  // The curtain is portaled to the anchorRef instead of `document.body` as the popover, since
  // we need the curtain to be positioned relative to the anchor element for correct placement.
  //
  // For reference, see `<SearchBox />` component.
  const CurtainPortal = useCurtain({
    show: isOpen,
    onClose,
    anchorRef
  });

  /**
   * Now, all the logic for computing the style of the popover
   * and positioning it relative to the anchor element is done in this `useEffect` hook.
   *
   * I didn't wanted to obfuscate the code too much inside the `useEffect` hook,
   * so I created a separate function to compute the style of the popover.
   *
   * The documentation for the function is inside the `useEffect` hook, but it can be quite
   * long, so I will summarize it here.
   *
   * TL;DR:
   * - The function computes the style of the popover based on the position and direction
   *   of the popover relative to the anchor element.
   * - Adds to the `requestAnimationFrame` queue to ensure that the DOM is updated before
   *   the calculations are made.
   * - Performs the calculations in the next animation frame to update the UI.
   * - Subscribes to anchor and content elements for any changes that might affect the position of the popover.
   * - Cleans up the event listeners and observers when the component unmounts.
   */
  useEffect(() => {
    /**
     * This function computes the style of the popover based on the position and direction
     * of the popover relative to the anchor element.
     *
     * It uses the `getBoundingClientRect` method to get the position of the anchor element
     * and the popover content element, and then calculates the top and left properties
     * based on the position and direction of the popover.
     *
     * It runs in the next animation frame to ensure that the DOM is updated before
     * the calculations are made.
     *
     * @returns The ID of the animation frame.
     */
    function computeStyle(): number {
      // Since the computation is not trivial, we use requestAnimationFrame to
      // schedule the computation to be executed in the next frame.
      //
      // Then we get the `frameId` and use it to cancel the animation frame in case the component unmounts
      // or the popover is closed before the animation frame is executed.
      return requestAnimationFrame(() => {
        // To avoid unnecessary calculations, we check if the popover is open
        // and if the anchorRef and contentRef are available.
        //
        // If not, we return early and do not perform any calculations.
        if (
          !isOpen ||
          isNullish(anchorRef.current) ||
          isNullish(contentRef.current)
        ) {
          return;
        }

        // Get the bounding rectangles of the anchor and popover content elements.
        const anchorRect = anchorRef.current.getBoundingClientRect();
        const popoverRect = contentRef.current.getBoundingClientRect();

        // To help with positioning, we need to calculate the anchor point
        // based on the position of the anchor element
        const anchorPoint = {
          x: position.x === 'left' ? anchorRect.left : anchorRect.right,
          y: position.y === 'top' ? anchorRect.top : anchorRect.bottom
        };

        // After calculating the anchor point, we need to adjust the top and left
        // properties of the popover based on the direction of growth on x and y axis.
        const left =
          direction.x === 'left'
            ? anchorPoint.x - popoverRect.width // Grow left by reducing width of the popover of the anchor point
            : anchorPoint.x; // Grow right by keeping the anchor point as is

        // Y-axis positioning
        const top =
          direction.y === 'top'
            ? anchorPoint.y - popoverRect.height // Grow upward by reducing height of the popover of the anchor point
            : anchorPoint.y; // Grow downward by keeping the anchor point as is

        setComputedStyle({
          // Set the position of the popover to absolute
          position: 'absolute',
          // Rounding the top and left properties to avoid subpixel rendering
          // and to ensure that the popover is positioned correctly and avoid flickering
          top: `${Math.round(top)}px`,
          left: `${Math.round(left)}px`
        });
      });
    }

    // Perform the computation if any of the dependencies changes and retrieve the
    // frameId to cancel the animation frame in case the component unmounts
    const frameId = computeStyle();

    // We need to observe the anchor and content elements for any changes
    // that might affect the position of the popover.
    //
    // We use ResizeObserver to observe the anchor and content elements
    // and recalculate the position of the popover when they change.
    //
    // We only create the observers if the elements are available in the DOM.
    const anchorObserver = anchorRef.current
      ? new ResizeObserver(computeStyle)
      : null;
    const contentObserver = contentRef.current
      ? new ResizeObserver(computeStyle)
      : null;

    anchorObserver?.observe(anchorRef.current!);
    contentObserver?.observe(contentRef.current!);

    // We also need to listen for the resize event on the window
    // to recalculate the position of the popover when the window is resized.
    window.addEventListener('resize', computeStyle);

    // Cleanup function to cancel the animation frame in case the component unmounts
    // or the popover is closed before the animation frame is executed.
    return () => {
      cancelAnimationFrame(frameId);
      window.removeEventListener('resize', computeStyle);
      anchorObserver?.disconnect();
      contentObserver?.disconnect();
    };
  }, [isOpen, anchorRef, contentRef, position, direction]);

  return (
    <>
      {CurtainPortal}
      {createPortal(
        <CSSTransition
          appear={isOpen}
          in={isOpen}
          nodeRef={popoverRef}
          onExited={onCloseFinished}
          mountOnEnter
          unmountOnExit
          timeout={popoverTransitionTimeout}
          classNames={{
            // States for when the popover is mounted to the DOM.
            // Considered direction.y as the vertical direction to apply proper animation.
            appear: S[`popover-enter-${direction.y}`],
            appearActive: S[`popover-enter-active`],
            appearDone: S[`popover-enter-done`],
            // States for when the popover being shown
            enter: S[`popover-enter`],
            enterActive: S[`popover-enter-active`],
            enterDone: S[`popover-enter-done`],
            // States for when the popover is being hidden and about to be removed from the DOM.
            // Considered direction.y as the vertical direction to apply proper animation.
            exit: S[`popover-exit`],
            exitActive: S[`popover-exit-active-${direction.y}`],
            exitDone: S[`popover-exit-done-${direction.y}`]
          }}
        >
          <div
            // Same ref as the CSSTransition nodeRef
            ref={popoverRef}
            className={classes(
              S[`popover-enter-${direction.y}`],
              popoverClassName
            )}
            role="dialog"
            aria-modal="true"
          >
            <div
              ref={contentRef}
              className={classes(S.content, className)}
              // Computed styles will be applied to the popover content element.
              style={computedStyle}
            >
              {children}
            </div>
          </div>
        </CSSTransition>,
        // The popover is mounted to the body element to ensure it is positioned
        // correctly in viewport coordinates. This is necessary because CSS transforms
        // on ancestor elements (like the header's translateY when hiding the utility bar)
        // create a new containing block that would break fixed/absolute positioning
        // if the popover were rendered inside the transformed element.
        document.body
      )}
    </>
  );
};
